import { supabase } from '../lib/supabaseClient';

interface Actor {
  id?: string | number | null;
  name?: string | null;
}

/**
 * Mark a conversation handled. Team-wide, like every other inbox state here —
 * one row per conversation, not one per person.
 */
export async function markDone(conversationKey: string, actor: Actor): Promise<void> {
  const { error } = await supabase.from('inbox_dismissals').upsert({
    conversation_key: conversationKey,
    dismissed_at: new Date().toISOString(),
    dismissed_by_user_id: actor.id ?? null,
    dismissed_by_name: actor.name ?? null,
    snoozed_until: null,
  });
  if (error) throw error;
}

/**
 * Hide a conversation until `until`, then let it come back needing a reply.
 * The same row as a dismissal, with an expiry — so "not now" can't quietly
 * become "done".
 */
export async function snooze(
  conversationKey: string,
  until: Date,
  actor: Actor,
): Promise<void> {
  const { error } = await supabase.from('inbox_dismissals').upsert({
    conversation_key: conversationKey,
    dismissed_at: new Date().toISOString(),
    dismissed_by_user_id: actor.id ?? null,
    dismissed_by_name: actor.name ?? null,
    snoozed_until: until.toISOString(),
  });
  if (error) throw error;
}

/** Put a conversation back in the queue (undo a done or a snooze). */
export async function reopen(conversationKey: string): Promise<void> {
  const { error } = await supabase
    .from('inbox_dismissals')
    .delete()
    .eq('conversation_key', conversationKey);
  if (error) throw error;
}

/**
 * Take a lead nobody owns. First click wins: the write only lands while
 * owner_id is still empty — the Slack "I'll take it" rule — so two staff
 * taking it at once can't both get it, and a lead that got an owner meanwhile
 * (a reply is also a claim, via first touch) isn't taken from them.
 *
 * Resolves true if this call took it, false if someone already had it.
 */
export async function takeLead(leadId: string, userId: string): Promise<boolean> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('leads')
    .update({
      owner_id: userId,
      owner_assigned_at: now,
      owner_source: 'claim',
      updated_at: now,
    })
    .eq('id', leadId)
    .is('owner_id', null)
    .select('id');
  if (error) throw error;
  return (data?.length ?? 0) > 0;
}

/** Tomorrow at 9am local. */
export function tomorrowMorning(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

/** Later today — three hours from now, or 9am tomorrow if that's past 6pm. */
export function laterToday(): Date {
  const d = new Date(Date.now() + 3 * 60 * 60 * 1000);
  if (d.getHours() >= 18) return tomorrowMorning();
  return d;
}
